import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { find, has } from 'lodash';
import {
  QUESTION_MULTIPLE,
  QUESTION_MATRIX_MAXDIFF,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
  GROUP_TYPE_CONJOINT,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_CONVERSATIONAL,
  PILCROW_REGEX,
  PILCROW_CHARACTER,
} from '../../constants';
import {
  escapeHTML,
  doesAnswersContainsOnlyEmoji,
  transformPrompt,
} from '../../utils';
import AnswerWithImage from './AnswerWithImage';
import AttributeValues from './AttributeValues';
import { getAnswerClass } from './utils';
import { FocusElement } from '../../helpers/focusElement';

export default function Answers({ question, answers = [] }) {
  const questionType = question.style || question.type;
  const shouldRender =
    questionType !== QUESTION_MATRIX_MAXDIFF &&
    questionType !== QUESTION_MATRIX_BIPOLAR &&
    questionType !== QUESTION_DRILLDOWN &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED_NUMERIC &&
    questionType !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM &&
    questionType !== QUESTION_CONVERSATIONAL;

  const [value, setValue] = useState('');
  const otherInputRef = useRef(null);
  const otherContainerRef = useRef(null);
  const containsOnlyEmoji = useMemo(
    () => doesAnswersContainsOnlyEmoji(answers),
    [answers],
  );

  useEffect(() => {
    const otherContainerElement = otherContainerRef?.current;
    if (otherContainerElement) {
      const questionWrapper = Pollfish.ui.getClosestQuestionWrapperElement(
        otherContainerElement,
      );
      const isDisplaySamePage = Pollfish.ui.isQuestionDisplaySamePage(
        questionWrapper,
      );

      const otherInputElement = otherInputRef?.current;
      $(otherContainerElement).hide();

      if (isDisplaySamePage) {
        const otherAnswerSelection = $(questionWrapper).find('.answer-other');
        FocusElement($(otherInputElement), otherAnswerSelection);
      } else {
        FocusElement($(otherInputElement));
      }
    }
  }, []);

  const handleTap = useCallback(
    (event) => {
      let answerEl = $(event.target);
      const questionWrapper = Pollfish.ui.getClosestQuestionWrapperElement(
        answerEl,
      );
      const isDisplaySamePage = Pollfish.ui.isQuestionDisplaySamePage(
        questionWrapper,
      );

      if (!answerEl.hasClass('answer')) {
        answerEl = answerEl.closest('div.answer');
      }
      const otherContainer = $(otherContainerRef?.current);

      // we can select only one exclusive answer per question
      // this feature is allowed only in multiple select
      if (answerEl.hasClass('exclusive-answer')) {
        // unselect selected answers
        answerEl.siblings().removeClass('answerClick');
        otherContainer.hide();
      } else if (answerEl.hasClass('answer--multi-choice')) {
        $(questionWrapper)
          .find('.exclusive-answer')
          .removeClass('answerClick');
        // if we select another answer and other answer is exclusive
        // we should hide the other input
        if (
          $(questionWrapper)
            .find('.answer-other')
            .hasClass('exclusive-answer')
        ) {
          otherContainer.hide();
        }
      }

      // if the question is not multi choice then remove
      // the previously selected answer
      if (
        !answerEl.hasClass('answer--multi-choice') ||
        Pollfish.event.clickedNoneOfTheAbove(answerEl) ||
        Pollfish.event.noneOfTheAboveSelected(questionWrapper)
      ) {
        // unselect selected answers
        $(questionWrapper)
          .find('.answerClick')
          .removeClass('answerClick');
        if (!Pollfish.event.clickedOtherAnswer(answerEl)) {
          otherContainer.hide();
        }

        if (
          !$('[data-btn="submit-btn"]').isOnScreen() &&
          Pollfish.helper.flag.showScrollDownMsg &&
          !isDisplaySamePage
        ) {
          Pollfish.helper.flag.showScrollDownMsg = false;
          Pollfish.ui.prompt(Pollfish.ui.getText('scroll_down'));
        }
      }

      answerEl.toggleClass('answerClick');

      if (Pollfish.event.clickedOtherAnswer(answerEl)) {
        const otherContainerCallback = () => {
          if (otherContainer.is(':visible') && !isDisplaySamePage) {
            const wrapper = $('html');
            wrapper.animate({
              scrollTop: otherContainer.offset().top + wrapper.scrollTop(),
            });
          }
        };

        if (questionType === QUESTION_MULTIPLE) {
          otherContainer.toggle(otherContainerCallback);
        } else {
          otherContainer.show(otherContainerCallback);
        }
      }

      if (
        answerEl.hasClass('answer--multi-choice') &&
        otherContainer.is(':visible')
      ) {
        $(otherContainerRef?.current).blur();
      }

      Pollfish.ui.validateQuestionAnswers(questionWrapper);
    },
    [questionType],
  );

  const onChange = useCallback((event) => {
    setValue(event.target.value);
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.keyCode === 13 && otherInputRef.current) {
      otherInputRef.current.blur();
    }
  }, []);

  const onKeyUp = useCallback(
    (event) => {
      if (event.key === PILCROW_CHARACTER) {
        setValue(value.replace(PILCROW_REGEX, ''));
      }
    },
    [value],
  );

  const onPaste = useCallback((event) => event.preventDefault(), []);

  const onClick = useCallback((event) => {
    event.preventDefault();
    if (otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, []);

  // if group type is max-diff do not proceed with render
  // since it will be handled by the specific question type
  if (!shouldRender) return null;

  let answerTemplate;
  const answersTemplate = [];
  // we keep those to handle straightlining
  Pollfish.store.setShuffledAnswers(question.refId || question.id, answers);

  let isConjoint = false;
  if (has(question, 'groupRefId')) {
    const group = find(Pollfish.survey.groups, ['refId', question.groupRefId]);
    if (group.type === GROUP_TYPE_CONJOINT) {
      isConjoint = true;
      const currentChoiceSet = group.getCurrentChoiceSet();
      if (!currentChoiceSet) {
        throw new Error('Missing a current concept set for group');
      }
      currentChoiceSet.conceptAnswers.forEach((conceptAnswer) => {
        const answerClass = getAnswerClass({
          question,
          containsOnlyEmoji,
        });
        let buttonProps = {};
        if (!answerClass.includes('hidden')) {
          buttonProps = { role: 'button', tabIndex: 0 };
        }
        answersTemplate.push(
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            id={conceptAnswer.id}
            key={conceptAnswer.id}
            className={`answer answer--conjoint ${answerClass} text-center`}
            onClick={handleTap}
            {...buttonProps}
          >
            <AttributeValues values={conceptAnswer.attributeValues} />
          </div>,
        );
      });

      // TODO: this needs handling for when we fix conjoint validations
      Pollfish.store.setShuffledAnswers(question.refId || question.id, [
        ...currentChoiceSet.conceptAnswers,
        ...Pollfish.store.getShuffledAnswers(question.refId),
      ]);
    }
  }
  answers.map((answer) => {
    const transformedBody = transformPrompt(answer.body);
    // if group type is conjoint do nothing and answer type is not none of the above
    if (isConjoint && answer.style !== ANSWER_NONE_OF_THE_ABOVE) {
      return true;
    }
    // if group type is conjoint and answer type non of the above
    if (isConjoint && answer.style === ANSWER_NONE_OF_THE_ABOVE) {
      const answerClass = getAnswerClass({
        question,
        answer,
        containsOnlyEmoji,
      });
      let buttonProps = {};
      if (!answerClass.includes('hidden')) {
        buttonProps = { role: 'button', tabIndex: 0 };
      }
      answersTemplate.push(
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          id={answer.id}
          key={`hidden-answer-${answer.id}`}
          className={`answer answer--conjoint ${answerClass} text-center`}
          onClick={handleTap}
          {...buttonProps}
        >
          <div className="padding-md-horizontal padding-md-top padding-md-btm answer--conjoint-wrapper">
            <span className="answer--conjoint-color">
              {escapeHTML(transformedBody) || '&nbsp;'}
            </span>
          </div>
        </div>,
      );
      return true;
    }
    const hasImageToShow = Boolean(
      answer.optimized_image_url || answer.image_url,
    );
    if (hasImageToShow) {
      answerTemplate = (
        <AnswerWithImage
          key={`answer-with-image-${answer.id}`}
          question={question}
          answer={answer}
          containsOnlyEmoji={containsOnlyEmoji}
          handleTap={handleTap}
        />
      );
    } else {
      const answerClass = getAnswerClass({
        question,
        answer,
        containsOnlyEmoji,
      });
      let buttonProps = {};
      if (!answerClass.includes('hidden')) {
        buttonProps = { role: 'button', tabIndex: 0 };
      }

      answerTemplate = (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          id={answer.id}
          key={`hidden-answer-${answer.id}`}
          className={`answer inline-children ${answerClass}`}
          onClick={handleTap}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: transformedBody || '&nbsp;' }}
          {...buttonProps}
        ></div>
      );
    }

    answersTemplate.push(answerTemplate);

    if (answer.style === ANSWER_OTHER) {
      answersTemplate.push(
        <div
          ref={otherContainerRef}
          className="otherContainer"
          key="other-container"
          role="button"
          tabIndex={0}
        >
          <textarea
            id={`otherContainerInput-${question.refId || question.id}`}
            className="input-text block"
            placeholder={answer.label}
            aria-label={answer.label}
            rows="1"
            dir="auto"
            ref={otherInputRef}
            onClick={onClick}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onPaste={onPaste}
          ></textarea>
        </div>,
      );
    }
    return true;
  });

  return <div className="answers">{answersTemplate}</div>;
}

Answers.propTypes = {
  question: PropTypes.object,
  answers: PropTypes.array,
};
